var _dec, _class, _Billboard;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { Heading } from '@instructure/ui-heading';
import { View } from '@instructure/ui-view';
import { omitProps, callRenderProp, getElementType } from '@instructure/ui-react-utils';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let Billboard = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_Billboard = class Billboard extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.handleClick = e => {
      const _this$props = this.props,
        readOnly = _this$props.readOnly,
        onClick = _this$props.onClick;
      if (readOnly) {
        e.preventDefault();
        e.stopPropagation();
      } else if (typeof onClick === 'function') {
        onClick(e);
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props2;
    (_this$props$makeStyle = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props2);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props3;
    (_this$props$makeStyle2 = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props3);
  }
  renderHeading() {
    const _this$props4 = this.props,
      headingLevel = _this$props4.headingLevel,
      headingAs = _this$props4.headingAs,
      heading = _this$props4.heading,
      styles = _this$props4.styles;
    return _jsx("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.heading,
      children: _jsx(Heading, {
        level: headingLevel,
        as: headingAs,
        color: "primary",
        children: heading
      })
    });
  }
  get SVGIconSize() {
    const size = this.props.size;

    // serve up appropriate SVGIcon size for each Billboard size
    if (size === 'small') {
      return 'medium';
    } else if (size === 'large') {
      return 'x-large';
    } else {
      return 'large';
    }
  }
  renderHero() {
    if (typeof this.props.hero === 'function') {
      return this.props.hero(this.SVGIconSize);
    } else {
      return this.props.hero;
    }
  }
  renderContent() {
    const _this$props5 = this.props,
      heading = _this$props5.heading,
      message = _this$props5.message,
      hero = _this$props5.hero,
      styles = _this$props5.styles;
    return _jsxs("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.content,
      children: [hero && _jsx("span", {
        css: styles === null || styles === void 0 ? void 0 : styles.hero,
        children: this.renderHero()
      }), heading && this.renderHeading(), message && _jsx("span", {
        css: styles === null || styles === void 0 ? void 0 : styles.message,
        children: callRenderProp(message)
      })]
    });
  }
  render() {
    const _this$props6 = this.props,
      href = _this$props6.href,
      disabled = _this$props6.disabled,
      readOnly = _this$props6.readOnly,
      margin = _this$props6.margin,
      styles = _this$props6.styles;
    const Element = getElementType(Billboard, this.props);
    return _jsx(View, {
      as: "div",
      margin: margin,
      children: _jsx(View, {
        ...omitProps(this.props, [...Billboard.allowedProps, ...View.allowedProps]),
        type: Element === 'button' ? 'button' : void 0,
        as: Element,
        elementRef: this.handleRef,
        css: styles === null || styles === void 0 ? void 0 : styles.billboard,
        href: href,
        onClick: this.handleClick,
        disabled: disabled,
        "aria-disabled": disabled || readOnly ? 'true' : void 0,
        children: this.renderContent()
      })
    });
  }
}, _Billboard.displayName = "Billboard", _Billboard.componentId = 'Billboard', _Billboard.propTypes = propTypes, _Billboard.allowedProps = allowedProps, _Billboard.defaultProps = {
  disabled: false,
  readOnly: false,
  size: 'medium',
  headingAs: 'span',
  headingLevel: 'h1',
  as: 'span',
  elementRef: () => {}
}, _Billboard)) || _class);
export default Billboard;
export { Billboard };